import '../../../assets/styles/AvatarOverlay.css'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { disableScreenShare } from '../../../App'
import AvatarDetails from '../../../components/AvatarDetails'
import { Meta } from '../../../components/Meta'
import { useArtifacts } from '../../../hooks/storeHooks/useArtifacts'
import { useSession } from '../../../hooks/storeHooks/useSessionState'
import { useMicrophones } from '../../../hooks/useMicrophones'
import { useScreenShare } from '../../../hooks/useScreenShare'
import { useSessionPiP } from '../../../hooks/useSessionPiP'
import { useSessionWebRTC } from '../../../hooks/useSessionWebRTC'
import { MentorBase, SessionMentor } from '../../../types/mentor'
import { OidcUser } from '../../../types/oidcUser'
import { analytics } from '../../../utils/analytics'
import { getOrganizationId } from '../../../utils/getOrganization'
import { avatarIdleVideos } from '../../../utils/utils'
// import Artifacts from '../Artifacts'
import { Controls } from '../Controls'
import { useComposite } from '../hooks/useComposite'
import { ScreenshotPanel } from '../Screenshot'
// import { SuggestedMentorsPanel } from '../SuggestedMentors'

import type { ContextByIdResponse } from '../../../api/session'
import type {
  UpdateBalanceEventData,
  WebRTCMessage,
} from '../../../types/webRtcMessage'
import { IdleModal } from '../IdleModal'

interface AvatarOverlayProps {
  personaData: SessionMentor | null
  mentorId: string
  prevMentorId: string
  prevSocketId: string | null
  freeTimeLeft: number
  isFreeSessionRunning: boolean
  contextData?: ContextByIdResponse['data']
  setPrevMentorId: (mentorId: string) => void
  setPrevSocketId: (socketId: string | null) => void
  startCountdown: () => void
}

export const AvatarOverlay: React.FC<AvatarOverlayProps> = ({
  personaData,
  mentorId,
  prevMentorId,
  prevSocketId,
  contextData,
  freeTimeLeft,
  isFreeSessionRunning,
  setPrevMentorId,
  setPrevSocketId,
  startCountdown,
}) => {
  const idleVideo = avatarIdleVideos[mentorId] || null
  const startTimeRef = useRef<number | null>(null)

  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const contextParam = searchParams.get('context')

  const [avatarTalkingState, setAvatarTalkingState] = useState('')
  const [showAvatarDetails, setShowAvatarDetails] = useState(true)

  const {
    avatarId,
    avatarReady,
    transcript,
    secondsBalance,
    sessionId,
    dataChannel,
    isFileUploading,
    setAvatarReady,
    addTranscript,
    setPromptData,
    setSecondsBalance,
    setCreditBalance,
    setAvatarId,
    setIsFileUploading,
    socketId,
  } = useSession()

  const { artifactPanelOpen, setSuggestedMentors } = useArtifacts()

  const { user, isAuthenticated } = useAuth()
  const videoRef = useRef<HTMLVideoElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)

  const handleWebRTCMessage = (data: WebRTCMessage) => {
    console.log('WebRTC Message:', data)
    switch (data.event) {
      case 'avatar_state_changed':
        if (data.data?.state === 'ready') {
          setAvatarReady(true)
        }
        break
      case 'message_received': {
        const { message } = data.data || {}
        if (
          message?.action === 'completed' &&
          typeof message.content === 'string' &&
          message.role
        ) {
          console.log('Message received:', message.content)
          addTranscript({
            role: message.role,
            content: message.content,
          })
        }
        if (
          (message?.type === 'function_call_output' ||
            message?.type === 'function_call') &&
          message?.content
        ) {
          const raw = JSON.parse(message.content) as string
          console.log('Function call output:', JSON.parse(raw) as unknown)
        }
        break
      }
      case 'talk_state_changed': {
        const newState = data.data?.state || ''
        setAvatarTalkingState(newState)
        setCompositeTalkingState(newState)

        // Send real-time update to PiP window
        const newIsAvatarTalking =
          !!newState && newState !== 'ended' && newState !== 'canceled'
        sendMessageToPiP({
          type: 'avatarTalkingStateChanged',
          data: { isAvatarTalking: newIsAvatarTalking },
        })
        console.log('Avatar talking state changed:', newState)

        if (
          contextParam &&
          data.data?.sentence_index !== undefined &&
          (data.data?.state === 'started' || data.data?.state === 'in_progress')
        ) {
          handleSetSentence(data.data.sentence_index)
        }
        break
      }
      case 'persona-prompt':
        if (data?.status === 'Success') {
          setPromptData(data.answer || '')
        }
        break
      case 'back_channel_message_received': {
        const type = data.data?.type
        if (type === 'update-balance') {
          const balanceData = data.data
            ?.data as unknown as UpdateBalanceEventData
          if (balanceData?.credit_usage) {
            setSecondsBalance(
              (balanceData.credit_usage.subscription_details?.balance ?? 0) +
                (balanceData.credit_usage.pack_details?.balance ?? 0),
            )
            setCreditBalance(
              (balanceData.credit_usage.subscription_details?.creditBalance ??
                0) +
                (balanceData.credit_usage.pack_details?.creditBalance ?? 0),
            )
          }
        }
        if (type === 'suggested-mentors') {
          const mentorsData = data.data?.data as unknown as MentorBase[]
          setSuggestedMentors(
            mentorsData.filter(
              (mentor: MentorBase) => mentor.mentorId !== personaData?.id,
            ),
          )
        }
        break
      }
      default:
        break
    }
  }

  useEffect(() => {
    sendMessageToPiP({
      type: 'fileUploadStateChanged',
      data: { isFileUploading },
    })
  }, [isFileUploading])

  const trackSessionDuration = () => {
    if (startTimeRef.current) {
      const endTime = Date.now()
      const sessionDuration = Math.round(
        (endTime - startTimeRef.current) / 1000,
      )

      analytics.logEvent(
        contextParam ? 'watch_session_ended' : 'regular_session_ended',
        {
          session_duration: sessionDuration,
          companion_id: mentorId,
          companion_slug: personaData?.slug || '',
        },
      )
    }
  }

  const orgId = getOrganizationId(user as OidcUser)

  const {
    isMuted,
    stream: userMicrophoneStream,
    selectedMicrophone,
    connectMicrophone,
    muteMicrophone,
  } = useMicrophones(!!contextParam)

  const {
    videoStream,
    audioStream,
    isWebRtcConnected,
    stopWebrtc,
    screenShareCaptureRef,
  } = useSessionWebRTC({
    avatarId,
    userMicrophoneStream,
    selectedMicrophone,
    mentorId,
    prevMentorId: prevMentorId === mentorId ? null : prevMentorId,
    prevSocketId: socketId === prevSocketId ? null : prevSocketId,
    context: contextParam || '',
    companionSlug: personaData?.slug,
    isAuthenticated,
    onMessage: handleWebRTCMessage,
  })

  const {
    visualData,
    compositeProgress,
    isCompositeEnded,
    estimatedSpeakingTime,
    setOpenAiVoice,
    handleSetSentence,
    handleRepeatCurrentSpeech,
    setCompositeTalkingState,
  } = useComposite({ addTranscript, avatarReady })

  // Screen sharing functionality
  const {
    isCapturing: isScreenSharing,
    toggleScreenShare,
    stopScreenShare,
    startScreenShare,
    isSupported,
  } = useScreenShare(screenShareCaptureRef.current)

  const handleScreenShare = async () => {
    await toggleScreenShare(dataChannel)
  }

  const handleStartScreenShare = useCallback(async () => {
    await startScreenShare(dataChannel)
  }, [startScreenShare, dataChannel])

  const handleStopScreenShare = useCallback(() => {
    stopScreenShare(dataChannel)
  }, [stopScreenShare, dataChannel])

  useEffect(() => {
    if (isCompositeEnded) {
      analytics.logEvent('watch_session_played_completely')
    }
  }, [isCompositeEnded])

  const isAvatarTalking = useMemo(
    () =>
      !!avatarTalkingState &&
      avatarTalkingState !== 'ended' &&
      avatarTalkingState !== 'canceled',
    [avatarTalkingState],
  )

  // Audio sync callbacks for PiP
  const handlePiPVolumeChange = useCallback((volume: number) => {
    // Dispatch custom event to notify AudioControl components
    window.dispatchEvent(
      new CustomEvent('audioControlSync', {
        detail: { volume, muted: audioRef.current?.muted ?? false },
      }),
    )
  }, [])

  const handlePiPMuteChange = useCallback((muted: boolean) => {
    // Dispatch custom event to notify AudioControl components
    window.dispatchEvent(
      new CustomEvent('audioControlSync', {
        detail: { volume: audioRef.current?.volume ?? 1, muted },
      }),
    )
  }, [])

  const handlePiPMicMuteChange = useCallback(
    (micMuted: boolean) => {
      // Apply microphone mute state directly
      muteMicrophone(micMuted)
    },
    [muteMicrophone],
  )

  const handlePiPStopTalking = useCallback(() => {
    if (dataChannel && dataChannel.readyState === 'open') {
      const data = {
        type: 'cancel',
        data: { sessionId },
      }
      dataChannel.send(JSON.stringify(data))
    }
  }, [dataChannel, sessionId])

  const handleTrackUploadEvent = useCallback(
    (event: string) => {
      setIsFileUploading(event === 'upload_started')
    },
    [setIsFileUploading],
  )

  // Separate function for stopping session from PiP window
  const stopSessionFromPiP = useCallback(() => {
    // Remove start=true from URL to prevent auto-restart (like IdleModal does)
    const currentParams = new URLSearchParams(location.search)
    currentParams.delete('start')
    const newUrl = `${location.pathname}${currentParams.toString() ? `?${currentParams.toString()}` : ''}`
    navigate(newUrl, { replace: true })

    // Stop the session
    setAvatarReady(false)
    setAvatarId('')

    if (isWebRtcConnected) {
      stopScreenShare(dataChannel)
      stopWebrtc()
    }

    trackSessionDuration()
  }, [
    location.search,
    location.pathname,
    navigate,
    setAvatarReady,
    setAvatarId,
    isWebRtcConnected,
    stopWebrtc,
    trackSessionDuration,
    stopScreenShare,
    dataChannel,
  ])

  // Picture-in-Picture functionality for session
  const { isPiPSupported, sendMessageToPiP } = useSessionPiP({
    isWatchSession: !!contextData,
    isScreenSharing,
    hideScreenShareButton: !!contextData || !isSupported,
    videoElement: videoRef.current,
    isSessionActive: avatarReady && isWebRtcConnected,
    hasMediaStream: !!videoStream || !!audioStream,
    audioRef,
    initialVolume: audioRef.current?.volume ?? 1,
    initialMuted: audioRef.current?.muted ?? false,
    personaData,
    // Pass current avatar state for sync
    isAvatarTalking: isAvatarTalking,
    avatarReady: avatarReady,
    onVolumeChange: handlePiPVolumeChange,
    onMuteChange: handlePiPMuteChange,
    onMicMuteChange: handlePiPMicMuteChange,
    onEndSessionComplete: stopSessionFromPiP,
    isMuted,
    muteMicrophone,
    userMicrophoneStream,
    startScreenShare: handleStartScreenShare,
    stopScreenShare: handleStopScreenShare,
    // Upload props
    isAuthenticated,
    avatarId: personaData?.avatar_id,
    organizationId: orgId,
    // Upload state sync
    isFileUploading,
    // Stop talking props
    onStopTalking: handlePiPStopTalking,
    dataChannel,
    sessionId,
    onTrackUploadEvent: handleTrackUploadEvent,
    socketId: socketId || '',
  })

  useEffect(() => {
    sendMessageToPiP({
      type: 'shareStateChanged',
      data: { shareScreenState: isScreenSharing },
    })
  }, [isScreenSharing])

  const stopSessions = useCallback(
    (withRedirect: boolean) => {
      stopScreenShare(dataChannel)
      if (!avatarReady && withRedirect) {
        navigate(
          `${location.pathname}${contextParam ? `?context=${contextParam}` : ''}`,
        )
      }
      setAvatarReady(false)
      setAvatarId('')

      if (isWebRtcConnected) {
        stopWebrtc()
      }

      trackSessionDuration()
    },
    [isWebRtcConnected, stopScreenShare, dataChannel],
  )

  const handleUpdateCookie = () => {
    window.Cookiebot?.renew()
  }

  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = videoStream || null
  }, [videoStream])

  useEffect(() => {
    if (audioRef.current) audioRef.current.srcObject = audioStream || null
  }, [audioStream])

  useEffect(() => {
    connectMicrophone().catch(error => {
      console.error('Error connecting microphone:', error)
    })

    return () => {
      setIsFileUploading(false)
      stopSessions(false)
      setSuggestedMentors(null)
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated && isWebRtcConnected) {
      startCountdown()
      startTimeRef.current = Date.now()
    }

    if (mentorId !== prevMentorId) {
      setPrevMentorId(mentorId)
    }

    if (socketId && socketId !== prevSocketId) {
      setPrevSocketId(socketId)
    }

    if (isWebRtcConnected) {
      analytics.logEvent(
        contextParam ? 'watch_session_started' : 'regular_session_started',
        {
          companion_id: mentorId,
          companion_slug: personaData?.slug || '',
        },
      )
    }
  }, [isWebRtcConnected, contextParam, isAuthenticated])

  useEffect(() => {
    if (
      isWebRtcConnected &&
      ((isAuthenticated && secondsBalance === 0) ||
        (!isAuthenticated && freeTimeLeft === 0))
    ) {
      stopSessions(true)
    }
  }, [
    secondsBalance,
    isAuthenticated,
    isWebRtcConnected,
    freeTimeLeft,
    stopSessions,
  ])

  useEffect(() => {
    if (avatarReady) {
      setTimeout(() => {
        setShowAvatarDetails(false)
      }, 10000)
    }
  }, [avatarReady])

  return (
    <>
      <div className="avatar-overlay">
        {videoStream && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            disablePictureInPicture={!isPiPSupported}
            data-testid="avatar-main-video"
            className={`talk-video avatar-video ${(isAvatarTalking && idleVideo) || !idleVideo ? 'opacity-100' : 'opacity-0'}`}
          />
        )}
        {audioStream && <audio ref={audioRef} autoPlay />}
        {idleVideo && (
          <video
            src={idleVideo}
            autoPlay
            loop
            playsInline
            muted
            disablePictureInPicture
            data-testid="avatar-idle-video"
            className={`idle-video avatar-video ${isAvatarTalking ? 'opacity-0' : 'opacity-100'}`}
          />
        )}
        {avatarReady && (
          <>
            <div
              className={`absolute bottom-0 left-0 w-full z-50 ${artifactPanelOpen ? 'hidden md:flex flex-col' : 'flex flex-col'}`}
            >
              <Controls
                isTalking={isAvatarTalking}
                hasNextSpeech={!isCompositeEnded}
                isMuted={isMuted}
                personaData={personaData}
                userMicrophoneStream={userMicrophoneStream}
                selectedMicrophone={selectedMicrophone || null}
                contextParam={contextParam}
                compositeProgress={compositeProgress}
                audioRef={audioRef}
                estimatedSpeakingTime={estimatedSpeakingTime}
                isAuthenticated={isAuthenticated}
                setMuted={muteMicrophone}
                setOpenAiVoice={setOpenAiVoice}
                handleRepeatCurrentSpeech={handleRepeatCurrentSpeech}
                onScreenShare={handleScreenShare}
                isScreenSharing={isScreenSharing}
                showScreenShareButton={!disableScreenShare && isSupported}
                controls
              />
              <div className="text-10px mb-0 md:mb-1.5 w-full text-white bottom-0 text-center mt-2 mx-auto max-w-[290px] px-4">
                AI assistant, not human or licensed professional.
                <br />
                May make mistakes. See&nbsp;
                <button
                  className="text-white text-10px underline flex-1"
                  onClick={handleUpdateCookie}
                >
                  Cookie settings
                </button>
              </div>
            </div>
            <AvatarDetails
              mentor={personaData!}
              title={contextData?.title}
              className={`fixed z-20 bottom-[135px] md:bottom-[140px]  xl3:bottom-[50px] left-6 sm:left-10 !w-fit max-w-[300px] md:max-w-[500px] !mb-0 transition-opacity duration-300 ${
                showAvatarDetails ? '' : 'opacity-0 pointer-events-none'
              }`}
            />
          </>
        )}
      </div>
      {/* {avatarReady && transcript && (
        <Artifacts
          mentorId={mentorId}
          setShowAvatarDetails={setShowAvatarDetails}
          isWatchSession={!!contextParam}
        />
      )} */}
      {visualData?.length && avatarReady && (
        <ScreenshotPanel visualData={visualData} />
      )}
      {/* <SuggestedMentorsPanel /> */}
      {personaData && (
        <Meta
          title={
            contextData?.title
              ? `${contextData?.title} - with ${personaData.firstName} ${personaData.lastName}`
              : `${personaData.firstName} ${personaData.lastName} - ${personaData.jobTitle}`
          }
          description={contextData?.outline || personaData?.summary}
          image={
            contextData?.imageUrl || personaData.avatar?.videos[0]?.preview_url
          }
          url={window.location.href}
          type="website"
          locale="en_US"
        />
      )}

      <IdleModal
        contextParam={contextParam}
        isSocketConnected={
          isAuthenticated ? isWebRtcConnected : isFreeSessionRunning
        }
        isWebRtcConnected={isWebRtcConnected}
        transcript={transcript}
      />
    </>
  )
}
